import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig, DecompositionNode }      from '../chart';
import { ChartHelper }                         from '../chart-helper';

interface DecompViewNode {
	x: number;
	y: number;            // top of the node block
	width: number;
	barWidth: number;
	depth: number;
	index: number;
	label: string;
	valueText: string;
	hasChildren: boolean;
	expanded: boolean;    // this node is the open one of its column
	text: string;         // tooltip: full label + exact value
}

interface Connector { d: string; }

/**
 * Decomposition tree (Power BI style): a measure broken down level by level, one
 * column per depth, each node a label + value + sibling-relative bar. The data is
 * the WHOLE tree the assistant provided; only one path is open at a time — by
 * default the highest-value child of each level — and clicking any node that has
 * children re-routes the open path to it. Interactive, but self-contained: no
 * re-querying, everything shown was in the spec.
 */
@Component({
	standalone:  false,
	selector:    '[cs-decomposition-chart]',
	templateUrl: './decomposition-chart.component.html'
})
export class CsDecompositionChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	columns: DecompViewNode[][] = [];
	connectors: Connector[] = [];
	nodeHeight = 30;

	/** Child index opened at each depth (path[0] = which child of the root is open). */
	private path: number[] = [];

	private isDataReady() {
		return !!this.config?.data?.tree;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.resetPath(); this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			// A new tree invalidates the old path; a pure resize keeps it.
			if (changes.config.previousValue?.data?.tree !== this.config.data.tree) { this.resetPath(); }
			this.calculate();
		}
	}

	/** Open a different branch: route the path through this node, then greedily onward. */
	onNodeClick(node: DecompViewNode) {
		if (!node.hasChildren || node.depth === 0) { return; }
		this.path = [...this.path.slice(0, node.depth - 1), node.index];
		this.extendPathGreedily();
		this.calculate();
	}

	/** Default open path: the highest-value child at every level (Power BI's default). */
	private resetPath() {
		this.path = [];
		this.extendPathGreedily();
	}

	private extendPathGreedily() {
		let node = this.config.data.tree;
		for (const idx of this.path) { node = node?.children?.[idx]; }
		while (node?.children?.length) {
			let best = 0;
			node.children.forEach((c, i) => {
				if (Math.abs(c.value) > Math.abs(node.children[best].value)) { best = i; }
			});
			this.path.push(best);
			node = node.children[best];
		}
	}

	private calculate() {
		const w = this.config.chartWidth  - this.config.xPadding * 2;
		const h = this.config.chartHeight - this.config.yPadding * 2;
		if (!w || !h || w < 40 || h < 40) { return; }

		// Column c shows the siblings at depth c along the open path.
		const levels: DecompositionNode[][] = [[this.config.data.tree]];
		let open = this.config.data.tree;
		for (const idx of this.path) {
			if (!open?.children?.length) { break; }
			levels.push(open.children);
			open = open.children[idx];
		}

		const colWidth  = w / levels.length;
		const nodeWidth = Math.max(60, colWidth - 30);
		const columns: DecompViewNode[][] = [];

		for (let d = 0; d < levels.length; d++) {
			const sibs    = levels[d];
			const maxAbs  = Math.max(...sibs.map(s => Math.abs(s.value)), 1e-9);
			const rowH    = Math.min(52, h / sibs.length);
			const blockTop = this.config.yPadding + Math.max(0, (h - rowH * sibs.length) / 2);
			columns.push(sibs.map((s, i) => ({
				x:           this.config.xPadding + d * colWidth,
				y:           blockTop + i * rowH + (rowH - this.nodeHeight) / 2,
				width:       nodeWidth,
				barWidth:    Math.max(2, Math.abs(s.value) / maxAbs * nodeWidth),
				depth:       d,
				index:       i,
				label:       this.truncate(s.label, nodeWidth),
				valueText:   ChartHelper.getValueFormatted(Math.round(s.value * 100) / 100),
				hasChildren: !!s.children?.length,
				expanded:    d === 0 || this.path[d - 1] === i,
				text:        `${s.label}\n${ChartHelper.getValueLocale(s.value)}`
			})));
		}

		// Connectors: open node of column d → every node of column d+1.
		const connectors: Connector[] = [];
		for (let d = 0; d + 1 < columns.length; d++) {
			const parent = columns[d].find(n => n.expanded);
			if (!parent) { continue; }
			const px = parent.x + parent.width + 4;
			const py = parent.y + this.nodeHeight / 2;
			for (const child of columns[d + 1]) {
				const cx = child.x - 4;
				const cy = child.y + this.nodeHeight / 2;
				const mid = (px + cx) / 2;
				connectors.push({ d: `M ${px} ${py} C ${mid} ${py} ${mid} ${cy} ${cx} ${cy}` });
			}
		}

		this.columns    = columns;
		this.connectors = connectors;
	}

	private truncate(label: string, width: number): string {
		const maxChars = Math.max(5, Math.floor(width / 6.2));
		const text = String(label ?? '');
		return text.length > maxChars ? text.slice(0, maxChars - 1).trimEnd() + '…' : text;
	}
}
